import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useSnackbar } from 'notistack';
import LeadsContext from '@/modules/leads/contexts/LeadsContext';
import Paper from '@/modules/common/components/Paper';
import Content from '@/modules/common/components/Content';
import Spinner from '@/modules/common/components/Spinner';
import Websocket from '@/modules/common/components/Websocket';
import ErrorFetch from '@/modules/common/components/ErrorFetch';
import Pagination from '@/modules/common/components/Pagination';
import ContentBody from '@/modules/common/components/ContentBody';
import ContentHeader from '@/modules/common/components/ContentHeader';
import { getCfgOffer, parseDate, formatDate } from '~/common';
import { withFetch } from '~/hocs/fetch';
import { withRouter } from '~/hocs/router';
import { actionDockSetExpandLeads } from '@/modules/application/actions/dock';
import Top from './Top/Top';
import Table from './Table/Table';
import DialogView from './DialogView/DialogView';
import DialogEdit from './DialogEdit';
import ContextMenu from './Table/ContextMenu';
import DialogDelete from './DialogDelete';
import '../../styles/Leads.scss';

const initialBreadcrumbs = [
    {name: 'Лиды'},
];

const initialCount = {
    count: 0,
    total: 0,
};

const initialCols = {
    type: true,
    advSub2: false,
    dts: true,
    transactionId: false,
};

const initialValuesFilter = {
    ids: '',
    advSub: '',
    offerName: [],
    affiliateName: [],
    typeName: [],
    date: {from: null, to: null},
    source: '',
    affSub: '',
    advSub2: '',
    dateStatus: {from: null, to: null},
    statusInternal: '',
    transactionId: '',
};

const mapValuesFilter = {
    ids: 'ids',
    advSub: 'adv_sub',
    source: 'source',
    affSub: 'aff_sub',
    advSub2: 'adv_sub2',
    statusInternal: 'status_internal',
    transactionId: 'transaction_id',
};

function isFiltered(urlSearchParams, checkOrdering = true) {
    const result = Object.entries(mapValuesFilter).some((element) => urlSearchParams.get(element[1])) ||
        urlSearchParams.get('date_from') ||
        urlSearchParams.get('date_to') ||
        urlSearchParams.get('date_status_from') ||
        urlSearchParams.get('date_status_to');
    return (checkOrdering) ? urlSearchParams.get('ordering') || result : result;
}

let abortController;

const Leads = (props) => {
    const offerName = props.router.getUrlSearchParam('offer_name');

    const { enqueueSnackbar } = useSnackbar();

    const [breadcrumbs, setBreadcrumbs] = useState(initialBreadcrumbs);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [count, setCount] = useState(initialCount);
    const [cols, setCols] = useState(initialCols);
    const [ordering, setOrdering] = useState('-datetime');
    const [valuesFilter, setValuesFilter] = useState(initialValuesFilter);
    const [_affiliates, setAffiliates] = useState([]);  // список партнеров по лидам
    const [selectedLead, setSelectedLead] = useState(null);
    const [openDialogView, setOpenDialogView] = useState(false);
    const [openDialogEdit, setOpenDialogEdit] = useState(false);
    const [openDialogDelete, setOpenDialogDelete] = useState(false);
    const [positionContextMenu, setPositionContextMenu] = useState({left: null, top: null});

    const fetch = () => {
        if (abortController) {
            abortController.abort();
        }
        abortController = new AbortController();
        setLoading(true);
        props.fetch.get(`/api/leads/?${props.router.urlSearchParams.toString()}`, abortController)
            .then(({ok, data}) => {
                if (ok) {
                    setData(data);
                    // noinspection JSUnresolvedVariable
                    setCount({count: data.count, total: data.total_count});
                }
                setLoading(false);
            })
            .catch((error) => {
                setData(null);
                if (error.name !== 'AbortError') {
                    setLoading(false);
                }
            })
            .finally(() => {
                abortController = null;
            });
    };

    const closeContextMenu = () => {
        setPositionContextMenu({left: null, top: null});
    };

    useEffect(() => {
        return () => {
            if (abortController) {
                abortController.abort();
            }
        };
    }, []);

    useEffect(() => {
        let bc = [...initialBreadcrumbs];
        let page = parseInt(props.router.urlSearchParams.get('page'));
        let ordering = props.router.urlSearchParams.get('ordering');
        const dateFrom = parseDate(props.router.getUrlSearchParam('date_from'), 'dd.MM.yy');
        const dateTo = parseDate(props.router.getUrlSearchParam('date_to'), 'dd.MM.yy');
        const dateStatusFrom = parseDate(props.router.getUrlSearchParam('date_status_from'), 'dd.MM.yy');
        const dateStatusTo = parseDate(props.router.getUrlSearchParam('date_status_to'), 'dd.MM.yy');

        if (!ordering) {
            ordering = '-datetime';
        }
        setOrdering(ordering);

        let _valuesFilter = {...initialValuesFilter};
        Object.entries(mapValuesFilter).forEach((element) => {
            _valuesFilter[element[0]] = props.router.getUrlSearchParam(element[1]);
        });
        _valuesFilter.offerName = props.router.getUrlSearchParam('offer_name').split('||');
        _valuesFilter.affiliateName = props.router.getUrlSearchParam('affiliate_name').split('||');
        _valuesFilter.typeName = props.router.getUrlSearchParam('type_name').split('||');
        _valuesFilter.date = {from: dateFrom, to: dateTo};
        _valuesFilter.dateStatus = {from: dateStatusFrom, to: dateStatusTo};
        setValuesFilter(_valuesFilter);

        if (isNaN(page)) {
            page = 1;
        }
        // noinspection JSCheckFunctionSignatures
        props.router.urlSearchParams.set('page', page);

        if (offerName) {
            const cfgOffer = getCfgOffer(props.cfgOffers, offerName);
            if (cfgOffer) {
                setCols({
                    type: cfgOffer.show_type_col,
                    advSub2: cfgOffer.show_adv_sub2_col,
                    dts: cfgOffer.show_dts_col,
                    transactionId: cfgOffer.show_transaction_id_col,
                });
            }
            if (props.cfgOffers.find((cfgOffer) => cfgOffer.show_in_leads_menu && cfgOffer.offer_name === offerName)) {
                props.dockSetExpandLeads(true);
                bc[0].to = '/leads/';
                bc.push({name: offerName});
            }
        }
        else {
            setCols(initialCols);
        }

        setBreadcrumbs(bc);
        fetch();
    }, [props.history.location.search]);

    useEffect(() => {
        if (data) {
            // noinspection JSUnresolvedVariable
            setCount({count: data.count, total: data.total_count});
        }
    }, [data]);

    const handleClickResetFilter = () => {
        props.history.push({search: '?page=1'});
    };

    const handleSubmitFilter = (event) => {
        event.preventDefault();
        props.router.urlSearchParams.set('ordering', '-datetime');
        Object.entries(mapValuesFilter).forEach((element) => {
            props.router.urlSearchParams.set(element[1], valuesFilter[element[0]]);
        });
        props.router.urlSearchParams.set('offer_name', valuesFilter.offerName.join('||'));
        props.router.urlSearchParams.set('affiliate_name', valuesFilter.affiliateName.join('||'));
        props.router.urlSearchParams.set('type_name', valuesFilter.typeName.join('||'));
        props.router.urlSearchParams.set('date_from', formatDate(valuesFilter.date.from));
        props.router.urlSearchParams.set('date_to', formatDate(valuesFilter.date.to));
        props.router.urlSearchParams.set('date_status_from', formatDate(valuesFilter.dateStatus.from));
        props.router.urlSearchParams.set('date_status_to', formatDate(valuesFilter.dateStatus.to));
        props.router.urlSearchParams.set('page', '1');
        props.history.push({search: props.router.urlSearchParams.toString()});
    };

    /******************************************************************************************************************/

    const handleContextMenu = (lead) => (event) => {
        event.preventDefault();
        setSelectedLead(lead);
        setPositionContextMenu({left: event.clientX, top: event.clientY});
    };

    const handleClickView = () => {
        closeContextMenu();
        setOpenDialogView(true);
    };

    const handleClickEdit = () => {
        closeContextMenu();
        setOpenDialogEdit(true);
    };

    const handleClickDelete = () => {
        closeContextMenu();
        setOpenDialogDelete(true);
    };

    const handleCloseContextMenu = () => {
        closeContextMenu();
        setSelectedLead(null);
    };

    /******************************************************************************************************************/

    const handleEditOk = (newLead) => {
        let results = data.results;
        results.forEach((lead) => {
            if (lead.id === newLead.id) {
                props.affiliates.forEach((affiliate) => {
                    if (affiliate.id === newLead.affiliate) {
                        lead.affiliate_id = affiliate.affiliate_id;
                        lead.affiliate_name = affiliate.affiliate_name;
                    }
                });
                if (newLead.type !== null) {
                    props.types.forEach((type) => {
                        if (type.id === newLead.type) {
                            lead.type = type;
                        }
                    });
                }
                else {
                    lead.type = newLead.type;
                }
                props.statuses.forEach((status) => {
                    if (status.id === newLead.status) {
                        lead.status = status;
                    }
                });
                lead.datetime_status = newLead.datetime_status;
                lead.comment = newLead.comment;
            }
        });
        setOpenDialogEdit(false);
        setData({...data, results: results});
        setSelectedLead(null);
        enqueueSnackbar('Лид обновлен', {variant: 'success'});
    };

    const handleDeleteOk = () => {
        setOpenDialogDelete(false);
        setSelectedLead(null);
        props.router.reload();
        enqueueSnackbar('Лид удален', {variant: 'success'});
    };

    /******************************************************************************************************************/

    // noinspection JSUnresolvedVariable
    return (
        <LeadsContext.Provider value={{
            count: count, setCount: (count) => setData({...data, ...count,}),
            cols: cols, ordering: ordering, setOrdering: setOrdering,
            valuesFilter: valuesFilter, setValuesFilter: setValuesFilter,
            isFiltered: (checkOrdering) => isFiltered(props.router.urlSearchParams, checkOrdering),
            affiliates: _affiliates,
        }}>
            <Content id="leads">
                <ContentHeader breadcrumbs={breadcrumbs}/>
                <ContentBody><Paper>
                    {!(loading)
                        ? <>
                            {(data && data.results)
                                ? <>
                                    <Top leads={data.results}
                                         onClickResetFilter={handleClickResetFilter}
                                         onSubmitFilter={handleSubmitFilter}
                                    />
                                    <Table leads={data.results} selectedLead={selectedLead} onContextMenu={handleContextMenu}/>
                                    {(data.results.length > 0)
                                        ? <>
                                            <ContextMenu position={positionContextMenu}
                                                         onClickView={handleClickView}
                                                         onClickEdit={handleClickEdit}
                                                         onClickDelete={handleClickDelete}
                                                         onClose={handleCloseContextMenu}
                                            />
                                            <DialogView selectedLead={selectedLead} setSelectedLead={setSelectedLead}
                                                        open={openDialogView} setOpen={setOpenDialogView}
                                            />
                                            <DialogEdit selectedLead={selectedLead} setSelectedLead={setSelectedLead}
                                                        open={openDialogEdit} setOpen={setOpenDialogEdit}
                                                        onOk={handleEditOk}
                                            />
                                            <DialogDelete selectedLead={selectedLead} setSelectedLead={setSelectedLead}
                                                          open={openDialogDelete} setOpen={setOpenDialogDelete}
                                                          onOk={handleDeleteOk}
                                            />
                                            <Pagination totalPages={data.total_pages} currentPage={data.current_page}/>
                                        </>
                                        : null
                                    }
                                </>
                                : <ErrorFetch onClick={() => fetch()}/>
                            }
                        </>
                        : <Spinner/>
                    }
                    {(!props.user.affiliate)
                        ? <Websocket path='/ws/leads/affiliates/'
                                     onMessage={(data) => setAffiliates(JSON.parse(data))}
                        />
                        : null
                    }
                </Paper></ContentBody>
            </Content>
        </LeadsContext.Provider>
    );
};

let Component = Leads;
Component = withRouter(Component);
Component = withFetch(Component);

export default connect(
    (state) => ({
        user: state.auth.user,
        types: state.types,
        statuses: state.statuses,
        cfgOffers: state.cfg.offers,
        affiliates: state.affiliates,
    }),
    (dispatch) => ({
        dockSetExpandLeads: bindActionCreators(actionDockSetExpandLeads, dispatch),
    })
)(Component);
